import { buildApiUrl } from '../../config/env';
import { storage } from '../../utils/storage';

// Types
export interface User {
  id: string;
  name: string | null;
  profession: string | null;
  photo: string | null;
}

export interface Club {
  id: string;
  name: string;
}

export interface Meetup {
  id: string;
  clubId: string;
  club: Club;
  creatorId: string;
  creator: User;
  recipientId: string;
  recipient: User;
  place: string;
  phone: string;
  type: 'One to One' | 'Group' | 'Conclave';
  dateTime: string | null;
  message: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt: string;
  updatedAt: string;
}

export interface MeetupsResponse {
  sent: Meetup[];
  received: Meetup[];
}

export interface CreateMeetupData {
  clubId: string;
  recipientId: string;
  place: string;
  phone: string;
  type: 'One to One' | 'Group' | 'Conclave';
  dateTime?: string;
  message?: string;
}

// ========================================
// CREATE MEETUP
// ========================================
export const createMeetup = async (data: CreateMeetupData): Promise<Meetup> => {
  const token = await storage.getToken();
  
  const response = await fetch(buildApiUrl('/api/meetups'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create meetup');
  }

  return response.json();
};

// ========================================
// GET MEETUPS
// ========================================
export const getMeetups = async (clubId?: string): Promise<MeetupsResponse> => {
  const token = await storage.getToken();
  
  const url = clubId 
    ? buildApiUrl(`/api/meetups?clubId=${clubId}`)
    : buildApiUrl('/api/meetups');

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch meetups');
  }

  return response.json();
};

// ========================================
// GET SINGLE MEETUP
// ========================================
export const getMeetup = async (id: string): Promise<Meetup> => {
  const token = await storage.getToken();
  
  const response = await fetch(buildApiUrl(`/api/meetups/${id}`), {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch meetup');
  }

  return response.json();
};

// ========================================
// UPDATE MEETUP STATUS
// ========================================
export const updateMeetupStatus = async (
  id: string,
  status: 'accepted' | 'rejected'
): Promise<Meetup> => {
  const token = await storage.getToken();
  
  const response = await fetch(buildApiUrl(`/api/meetups/${id}/status`), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update meetup status');
  }

  return response.json();
};

// ========================================
// ACCEPT MEETUP
// ========================================
export const acceptMeetup = async (id: string): Promise<Meetup> => {
  return updateMeetupStatus(id, 'accepted');
};

// ========================================
// REJECT MEETUP
// ========================================
export const rejectMeetup = async (id: string): Promise<Meetup> => {
  return updateMeetupStatus(id, 'rejected');
};
